import { useState, useEffect, useCallback } from 'react';
import { Machine, GlobalKPIs, ProductionPoint, MachineStatus } from '../types';

const OPERATORS = [
  { id: 'op1',  name: 'Ana Paula Silva',    initials: 'AP', avatarColor: '#6366f1', shift: 'Manhã' as const,  since: '07:30' },
  { id: 'op2',  name: 'Beatriz Santos',     initials: 'BS', avatarColor: '#ec4899', shift: 'Manhã' as const,  since: '07:30' },
  { id: 'op3',  name: 'Carla Mendes',       initials: 'CM', avatarColor: '#f59e0b', shift: 'Manhã' as const,  since: '07:45' },
  { id: 'op4',  name: 'Daniela Ferreira',   initials: 'DF', avatarColor: '#10b981', shift: 'Manhã' as const,  since: '08:00' },
  { id: 'op5',  name: 'Elaine Rodrigues',   initials: 'ER', avatarColor: '#3b82f6', shift: 'Manhã' as const,  since: '07:30' },
  { id: 'op6',  name: 'Fernanda Costa',     initials: 'FC', avatarColor: '#8b5cf6', shift: 'Tarde' as const,  since: '13:00' },
  { id: 'op7',  name: 'Gabriela Alves',     initials: 'GA', avatarColor: '#06b6d4', shift: 'Tarde' as const,  since: '13:00' },
  { id: 'op8',  name: 'Helena Lopes',       initials: 'HL', avatarColor: '#f97316', shift: 'Tarde' as const,  since: '13:15' },
  { id: 'op9',  name: 'Isabela Nascimento', initials: 'IN', avatarColor: '#14b8a6', shift: 'Tarde' as const,  since: '13:00' },
  { id: 'op10', name: 'Juliana Matos',      initials: 'JM', avatarColor: '#a855f7', shift: 'Tarde' as const,  since: '13:30' },
];

const MACHINE_CONFIGS = [
  { number: 1,  name: 'Overloque Principal',    model: 'Juki MO-6814S',    type: 'Overloque' as const,       baseTarget: 180, baseSpeed: 200 },
  { number: 2,  name: 'Reta Industrial #1',     model: 'Brother S-7220A',  type: 'Reta Industrial' as const, baseTarget: 220, baseSpeed: 240 },
  { number: 3,  name: 'Interlock Coleira',       model: 'Pegasus W664A',    type: 'Interlock' as const,       baseTarget: 160, baseSpeed: 180 },
  { number: 4,  name: 'Galoneira Dupla',         model: 'Juki MF-7923',     type: 'Galoneira' as const,       baseTarget: 140, baseSpeed: 155 },
  { number: 5,  name: 'Reta Industrial #2',      model: 'Pfaff 1245-706',   type: 'Reta Industrial' as const, baseTarget: 200, baseSpeed: 220 },
  { number: 6,  name: 'Caseadeira Automática',   model: 'Juki LBH-1790',    type: 'Caseadeira' as const,      baseTarget: 300, baseSpeed: 320 },
  { number: 7,  name: 'Botoneira Eletrônica',    model: 'Brother BE-438D',  type: 'Botoneira' as const,       baseTarget: 350, baseSpeed: 380 },
  { number: 8,  name: 'Travete Industrial',       model: 'Juki LK-1903B',    type: 'Travete' as const,         baseTarget: 400, baseSpeed: 430 },
  { number: 9,  name: 'Overloque #2',             model: 'Siruba F007K-W12', type: 'Overloque' as const,       baseTarget: 170, baseSpeed: 195 },
  { number: 10, name: 'Fechadeira de Cós',       model: 'Yamato VF2500',    type: 'Fechadeira' as const,      baseTarget: 130, baseSpeed: 145 },
  { number: 11, name: 'Interlock #2',             model: 'Pegasus EXT3216H', type: 'Interlock' as const,       baseTarget: 155, baseSpeed: 172 },
  { number: 12, name: 'Reta Industrial #3',       model: 'Juki DDL-9000C',   type: 'Reta Industrial' as const, baseTarget: 210, baseSpeed: 228 },
];

function fmtHHMM(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function generateHistory(baseSpeed: number, status: MachineStatus): ProductionPoint[] {
  const now = new Date();
  const points: ProductionPoint[] = [];
  let cumulative = 0;

  for (let i = 23; i >= 0; i--) {
    const t = new Date(now.getTime() - i * 30 * 60 * 1000);
    const label = fmtHHMM(t);

    let produced = 0;
    const halfHourlyRate = baseSpeed / 2;

    if (status === 'active') {
      produced = Math.round(halfHourlyRate * (0.75 + Math.random() * 0.4));
    } else if (status === 'idle') {
      produced = Math.round(halfHourlyRate * (0.1 + Math.random() * 0.3));
    } else if (status === 'maintenance' && i > 6) {
      produced = Math.round(halfHourlyRate * (0.6 + Math.random() * 0.3));
    }

    cumulative += produced;
    points.push({ time: label, produced: cumulative, target: Math.round(halfHourlyRate * (24 - i)) });
  }

  return points;
}

function buildMachines(): Machine[] {
  const statuses: MachineStatus[] = [
    'active','active','active','active','active','active','active',
    'idle','maintenance','offline','active','active',
  ];

  return MACHINE_CONFIGS.map((cfg, i) => {
    const status = statuses[i];
    const op = status === 'offline' || status === 'maintenance' ? null : OPERATORS[i % OPERATORS.length];
    const history = generateHistory(cfg.baseSpeed, status);
    const lastPoint = history[history.length - 1];
    const current = status === 'active'
      ? lastPoint.produced
      : status === 'idle' ? Math.round(lastPoint.produced * 0.6)
      : 0;
    const target = cfg.baseTarget * 12;
    const pct = Math.min(Math.round((current / target) * 100), 100);

    return {
      id: `maq-${cfg.number.toString().padStart(2, '0')}`,
      number: cfg.number,
      name: cfg.name,
      model: cfg.model,
      type: cfg.type,
      status,
      operator: op,
      production: {
        current,
        target,
        hourly: status === 'active' ? Math.round(cfg.baseSpeed * (0.8 + Math.random() * 0.3)) : 0,
        efficiency: pct,
      },
      uptime:
        status === 'active' ? 85 + Math.round(Math.random() * 14) :
        status === 'idle' ? 40 + Math.round(Math.random() * 30) : 0,
      temperature:
        status === 'active' ? 42 + Math.round(Math.random() * 18) :
        status === 'idle' ? 35 + Math.round(Math.random() * 8) : 28,
      history,
      lastMaintenance: '10/06/2026',
      nextMaintenance: '10/07/2026',
    };
  });
}

function computeKPIs(machines: Machine[]): GlobalKPIs {
  const active      = machines.filter(m => m.status === 'active').length;
  const idle        = machines.filter(m => m.status === 'idle').length;
  const maintenance = machines.filter(m => m.status === 'maintenance').length;
  const offline     = machines.filter(m => m.status === 'offline').length;
  const totalProduced = machines.reduce((s, m) => s + m.production.current, 0);
  const totalTarget   = machines.reduce((s, m) => s + m.production.target, 0);
  const activeMachines = machines.filter(m => m.status === 'active');
  const avgSpeed = activeMachines.length
    ? activeMachines.reduce((s, m) => s + m.production.hourly, 0) / activeMachines.length
    : 0;

  return {
    totalProduced,
    totalTarget,
    overallEfficiency: totalTarget > 0 ? Math.round((totalProduced / totalTarget) * 100) : 0,
    activeMachines: active,
    idleMachines: idle,
    maintenanceMachines: maintenance,
    offlineMachines: offline,
    averageSpeed: Math.round(avgSpeed),
  };
}

export function useMachineData() {
  const [machines, setMachines] = useState<Machine[]>(() => buildMachines());
  const [kpis, setKpis] = useState<GlobalKPIs>(() => computeKPIs(buildMachines()));
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const tick = useCallback(() => {
    setMachines(prev => {
      const updated = prev.map(m => {
        if (m.status !== 'active') return m;
        const increment = Math.max(0, Math.round(m.production.hourly / 3600 * 3 + (Math.random() - 0.1) * 2));
        const newCurrent = m.production.current + increment;
        const newHistory = [
          ...m.history.slice(1),
          {
            time: fmtHHMM(new Date()),
            produced: newCurrent,
            target: m.production.target,
          },
        ];
        return {
          ...m,
          production: {
            ...m.production,
            current: newCurrent,
            efficiency: Math.min(Math.round((newCurrent / m.production.target) * 100), 100),
          },
          history: newHistory,
        };
      });
      setKpis(computeKPIs(updated));
      setLastUpdate(new Date());
      return updated;
    });
  }, []);

  useEffect(() => {
    const id = setInterval(tick, 3000);
    return () => clearInterval(id);
  }, [tick]);

  return { machines, kpis, lastUpdate };
}
